/* eslint-disable */
/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';
/**
 * The layer of the stack a finding concerns.
 *
 * Eight values, matching the scanner's own categories exactly: one per check
 * family, each equal to the dotted prefix of every finding id under it, so a
 * client holding a `checkId` already knows which category it belongs to without
 * asking.
 *
 * They are declared in the order the scanner reads them, which is the order a
 * request travels: an address and the services listening on it, the name that
 * produced it, the transport carrying it, where the entry points send a browser,
 * and then three readings of the page it lands on. GraphQL enums carry no ordering
 * of their own, so a client that wants that sequence should render
 * `ScanSummary.byCategory` in the order the server returned it rather than sorting
 * the values.
 *
 * Treat an unrecognized value as an unnamed category and show it rather than
 * dropping the finding. New check families can introduce new categories.
 */
export type FindingCategory =
  /** DNS: delegation, SOA, CNAME, TTL, SPF, DMARC, DKIM, CAA, DNSSEC, TLSA. */
  | 'DNS'
  /**
   * Response security headers: CSP, framing, referrer policy, the cross-origin
   * family, permissions policy, and cookie attributes.
   */
  | 'HEADERS'
  /**
   * The homepage's own markup: mixed content, unpinned third-party subresources,
   * and what its tags and comments give away.
   */
  | 'HTML'
  /** Addressing: resolution, reverse DNS, reachability. */
  | 'IP'
  /**
   * Ports and the services behind them: which of the swept ports accept a
   * connection, and what the software answering on each one says it is.
   *
   * Declared next to `IP` rather than near `STACK` because what a machine is
   * listening on is a property of the address, not of the name that resolved to it.
   *
   * These findings appear only when the scan asked for a port sweep and the server
   * authorized it, so their absence says nothing about the target: read it as "not
   * asked", never as "nothing open". A client that offers the sweep should say
   * which it is.
   */
  | 'PORT'
  /**
   * Redirects: where each of a site's four entry points sends a browser, whether
   * they agree, and whether the chain reaches https without a plaintext hop.
   */
  | 'REDIRECT'
  /**
   * Software the response names or implies: CMS and framework fingerprinting,
   * disclosed versions graded against their support windows, `Server` headers.
   */
  | 'STACK'
  /** TLS: certificates, chains, protocol versions, ciphers, OCSP, CT, HSTS. */
  | 'TLS';

/**
 * The outcome of one check against one subject.
 *
 * Three-valued on purpose, and the single most important thing in this schema to
 * get right on the consuming side. A check that could not reach a verdict reports
 * UNKNOWN, which is not a pass and must not be rendered as one. Treat an
 * unrecognized value as UNKNOWN.
 */
export type FindingStatus =
  /** The check ran and the target is not fine on this point. */
  | 'FAIL'
  /** The check ran and the target is fine on this point. */
  | 'PASS'
  /** The check ran and could not decide. Not a pass. */
  | 'UNKNOWN';

/**
 * A named set of ports a scan may sweep.
 *
 * Each value is a short list of ports that answers a question an operator asks
 * about a host, rather than a slice of the port space. There is no ALL and there
 * will not be one: every port in a profile is one the scanner opens a connection
 * to on every resolved address, so a profile is a traffic multiplier, and a sweep
 * wide enough to interest an attacker is wide enough to get the operator's address
 * firewalled.
 *
 * The name is what the server writes into its authorization trail, and that is the
 * reason profiles exist rather than a port list on the input. "profile=DATABASE" is
 * an answer to an abuse report; nine port numbers is a list somebody has to decode
 * first.
 */
export type PortProfile =
  /** The union of the five above, for a machine somebody wants the inventory of. */
  | 'COMMON'
  /**
   * The control planes that command infrastructure: the Docker daemon's cleartext
   * socket and the Kubernetes API server. What an exposed Docker daemon grants is
   * the host rather than a login on it, which is why these two are their own
   * profile and not two more entries under REMOTE.
   */
  | 'CONTAINER'
  /**
   * Data stores. Redis and memcached historically shipped with no authentication
   * at all, and the rest are routinely deployed behind a firewall that later turns
   * out not to be there. Any of them answering from the public internet is a
   * finding before anything about how it is configured.
   */
  | 'DATABASE'
  /**
   * Submission, transport and mailbox access. Cleartext and implicit-TLS ports
   * both, since the finding worth having is a mailbox protocol reachable with no
   * TLS on offer at all.
   */
  | 'MAIL'
  /**
   * Administrative access to the machine itself: SSH, telnet, RDP, VNC, Windows
   * remote management, and Windows file sharing. Everything here except SSH has no
   * business facing the public internet, and SMB is the standard ransomware entry
   * path when it does.
   */
  | 'REMOTE'
  /**
   * HTTP and HTTPS on their standard and common alternate ports. The one profile
   * that overlaps what a scan already probes, and it earns its place because 8080
   * and 8443 are where a forgotten staging instance or an unproxied application
   * server usually answers.
   */
  | 'WEB';

export type RequestScanInput = {
  /**
   * The caller's assertion that they are authorized to have this host scanned.
   *
   * Must be true. A false value is refused with
   * `AUTHORIZATION_NOT_CONFIRMED`, and the assertion is recorded with the
   * request either way.
   *
   * This stops nobody who is determined, and it is not meant to. What it does is
   * put the claim on the record, so that the operator answering an abuse report
   * has something to answer with, and so that a visitor cannot say the tool
   * never asked. Present it as a deliberate act — a checkbox the visitor ticks,
   * not a constant the client sends.
   */
  confirmedAuthorized: boolean;
  /**
   * The caller's assertion that they are authorized to have this host's ports
   * swept.
   *
   * Defaults to false, which is what makes `portProfile` alone insufficient. It
   * is recorded with the request the same way `confirmedAuthorized` is, this time
   * alongside the profile that was asked for.
   *
   * It is a second assertion rather than a reuse of the first because it claims
   * something larger. `confirmedAuthorized` covers reading the target as a
   * website: the ports a browser uses, the names in its zone, the certificate it
   * serves. A sweep connects to ports nobody published — a database port, a
   * remote-access port — on every address behind the name, which is what the far
   * end's operator would call being port-scanned and what their intrusion
   * detection will file it as. Somebody who ticked one box to have their website
   * checked has not agreed to that, so it is asked for on its own and should be
   * presented on its own: a second deliberate act, worded so the visitor knows
   * what they are claiming.
   */
  confirmedPortScanAuthorized?: boolean;
  /**
   * A named set of extra ports to sweep on the target, or null to sweep none.
   *
   * Null is the ordinary request and what a client should send unless a visitor
   * deliberately asked for a port scan. With a profile named, the scan also opens
   * a TCP connection to every port that profile lists, on every address the name
   * resolves to, and reports what answered under the PORT category.
   *
   * A server accepts fewer profiles than this enum names. Whether any sweep is
   * offered at all, and which profiles, is the operator's decision, and it is not
   * published: a profile this deployment does not offer is refused with
   * `PORT_SCANNING_DISABLED`, which is also the answer when it offers none. A
   * client that presents a choice of profiles should be ready for that refusal on
   * any of them.
   *
   * `confirmedPortScanAuthorized` must be true alongside this. Naming a profile
   * without it is refused with `PORT_SCAN_AUTHORIZATION_NOT_CONFIRMED`, and a
   * refused sweep refuses the whole request rather than quietly scanning without
   * it — a report that silently answered a smaller question than the one asked
   * would be worse than a refusal.
   */
  portProfile?: PortProfile | null | undefined;
  /**
   * The host to scan: a DNS name, optionally with a port, such as "example.com"
   * or "example.com:443".
   *
   * A full URL is rejected. So is anything carrying whitespace, a control
   * character, a userinfo component, or a port outside the server's allow-list.
   * The server validates this string with the same parser the command line tool
   * uses and applies no other interpretation to it.
   *
   * Two things this field deliberately does not accept. It does not take an IP
   * literal, because the product scans websites and accepting bare addresses turns
   * a public form into a reconnaissance tool. It does not take a port list or an
   * address range, because both exist as options on the underlying scanner and
   * both would let a caller aim the scanner somewhere it is not meant to go.
   */
  target: string;
};

/**
 * How a scan ended.
 *
 * Only COMPLETE means every check reported. Treat any other value, including one
 * this client does not recognize, as an incomplete report: some checks may never
 * have run, and a target that looks clean under BUDGET_EXCEEDED has not been shown
 * to be clean.
 */
export type ScanOutcome =
  /**
   * The scan hit its time limit, thirty seconds by default, and was cut off. The
   * findings present are real. The ones missing are unknown, not absent.
   */
  | 'BUDGET_EXCEEDED'
  /** The scan was cancelled before it finished. */
  | 'CANCELLED'
  /** Every check ran to completion. */
  | 'COMPLETE'
  /**
   * The scan broke for a reason that is the server's fault rather than the
   * target's. The findings present, if any, are still real.
   */
  | 'FAILED';

/** Whether a scan has finished. Poll while it has not. */
export type ScanState =
  /** Nothing more will arrive. Read `outcome` to see whether it got that far. */
  | 'FINISHED'
  /** Accepted, not started. No findings yet. */
  | 'QUEUED'
  /** Checks are running. Findings are arriving. */
  | 'RUNNING';

/**
 * How much a failing finding should worry the reader.
 *
 * Listed from least to most urgent. GraphQL enums have no ordering, so a client
 * that needs a threshold should use `FindingFilter.minSeverity` and let the server
 * compare, rather than reimplementing the ranking.
 *
 * Carries no meaning when `Finding.status` is PASS.
 */
export type Severity =
  | 'CRITICAL'
  | 'HIGH'
  | 'INFO'
  | 'LOW'
  | 'MEDIUM';

/**
 * Reasons a scan request can be turned down.
 *
 * Treat an unrecognized value as a generic rejection and show `UserError.message`.
 * New codes are added as the server learns to distinguish cases it currently
 * lumps together, and a client that switches exhaustively over this enum will
 * break when that happens.
 */
export type UserErrorCode =
  /**
   * The caller did not confirm they are authorized to have the target scanned.
   *
   * `RequestScanInput.confirmedAuthorized` was false. Nothing about the target
   * was examined, so this says nothing about whether the target would have been
   * accepted.
   */
  | 'AUTHORIZATION_NOT_CONFIRMED'
  /** The server is at capacity. See `retryAfterSeconds`. */
  | 'CAPACITY_EXCEEDED'
  /**
   * This server will not sweep the ports that were asked for.
   *
   * Either the deployment offers no port sweep at all or it does not offer the
   * profile named, and both are answered with this one code deliberately. Which
   * profiles an operator permits is not a caller's business, and an answer that
   * distinguished the two would be an invitation to work through the enum until
   * one is accepted.
   *
   * No scan was started. A refused sweep refuses the request rather than falling
   * back to a scan without it, so a client that wants the report anyway asks
   * again with `portProfile` null.
   */
  | 'PORT_SCANNING_DISABLED'
  /**
   * The caller named a `RequestScanInput.portProfile` without setting
   * `confirmedPortScanAuthorized`.
   *
   * The profile itself was not looked at, so this says nothing about whether this
   * server would have swept those ports.
   */
  | 'PORT_SCAN_AUTHORIZATION_NOT_CONFIRMED'
  /** The caller has asked for too many scans. See `retryAfterSeconds`. */
  | 'RATE_LIMITED'
  /** The target is not a host this server will accept. The catch-all. */
  | 'TARGET_INVALID'
  /**
   * The target looks like a URL. Strip the scheme and the path and send the host
   * on its own. A client can offer this as a one-click correction.
   */
  | 'TARGET_LOOKS_LIKE_URL'
  /**
   * The target parses but this server will not scan it: an IP literal, or a name
   * the operator has excluded.
   */
  | 'TARGET_NOT_PERMITTED'
  /** The target names a port the server will not connect to. */
  | 'TARGET_PORT_NOT_ALLOWED'
  /** The target exceeds the server's length limit for a host. */
  | 'TARGET_TOO_LONG';

export type RequestScanMutationVariables = Exact<{
  input: RequestScanInput;
}>;


export type RequestScanMutation = { requestScan: { retryAfterSeconds: number | null, scan: { id: string, target: string, state: ScanState } | null, userErrors: Array<{ code: UserErrorCode, message: string, path: Array<string> }> } };

export type CheckCatalogueQueryVariables = Exact<{ [key: string]: never; }>;


export type CheckCatalogueQuery = { checks: Array<{ id: string, category: FindingCategory, description: string }> };

export type ScanReportQueryVariables = Exact<{
  id: string;
}>;


export type ScanReportQuery = { scan: { id: string, target: string, state: ScanState, outcome: ScanOutcome | null, requestedAt: string, startedAt: string | null, finishedAt: string | null, expiresAt: string, summary: { total: number, passed: number, failed: number, unknown: number, worstFailingSeverity: Severity | null, failuresBySeverity: Array<{ severity: Severity, count: number }>, byCategory: Array<{ category: FindingCategory, total: number, passed: number, failed: number, unknown: number }> }, findings: Array<{ key: string, checkId: string, category: FindingCategory, status: FindingStatus, severity: Severity, title: string, detail: string, risk: string | null, subject: string, remediation: string | null, refs: Array<string>, observedAt: string, evidence: Array<{ label: string, value: string }> }>, errors: Array<{ checkId: string, message: string }> } | null };

export class TypedDocumentString<TResult, TVariables>
  extends String
  implements DocumentTypeDecoration<TResult, TVariables>
{
  __apiType?: NonNullable<DocumentTypeDecoration<TResult, TVariables>['__apiType']>;
  private value: string;
  public __meta__?: Record<string, any> | undefined;

  constructor(value: string, __meta__?: Record<string, any> | undefined) {
    super(value);
    this.value = value;
    this.__meta__ = __meta__;
  }

  override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
    return this.value;
  }
}

export const RequestScanDocument = new TypedDocumentString(`
    mutation RequestScan($input: RequestScanInput!) {
  requestScan(input: $input) {
    scan {
      id
      target
      state
    }
    userErrors {
      code
      message
      path
    }
    retryAfterSeconds
  }
}
    `) as unknown as TypedDocumentString<RequestScanMutation, RequestScanMutationVariables>;
export const CheckCatalogueDocument = new TypedDocumentString(`
    query CheckCatalogue {
  checks {
    id
    category
    description
  }
}
    `) as unknown as TypedDocumentString<CheckCatalogueQuery, CheckCatalogueQueryVariables>;
export const ScanReportDocument = new TypedDocumentString(`
    query ScanReport($id: ID!) {
  scan(id: $id) {
    id
    target
    state
    outcome
    requestedAt
    startedAt
    finishedAt
    expiresAt
    summary {
      total
      passed
      failed
      unknown
      worstFailingSeverity
      failuresBySeverity {
        severity
        count
      }
      byCategory {
        category
        total
        passed
        failed
        unknown
      }
    }
    findings(first: 500) {
      key
      checkId
      category
      status
      severity
      title
      detail
      risk
      subject
      remediation
      refs
      observedAt
      evidence {
        label
        value
      }
    }
    errors {
      checkId
      message
    }
  }
}
    `) as unknown as TypedDocumentString<ScanReportQuery, ScanReportQueryVariables>;